import { QueryClient } from "@tanstack/react-query";
import { LoaderFunctionArgs, redirect } from "react-router-dom";

import {
  getDashboardBySlug,
  getFirstNavigationElementUrl,
} from "@/features/shared/navigation";
import { publishedDashboardsQuery } from "@/features/shared/hooks";
import { getIsAuthenticated } from "@/features/shared/features/authentication";
import { publishedNavigationElementsQuery } from "@/features/dashboard/features/dashboard";
import { ensurePublishedNavigationElement } from "@/features/dashboard/published-navigation";
import { ReturnTypeFromAsync } from "@/features/shared/types";
import { PublishedNavigationElement } from "@/lib/api";

const pageHasChildren = (page?: PublishedNavigationElement) => {
  if (!page) return false;

  return !!page.children?.length;
};

const assert = (condition: boolean, message = "", statusCode = 404) => {
  if (!condition) {
    throw new Response(message, {
      status: statusCode,
    });
  }
};

/**
 * Looks a dashboard up in the published list, refetching once when the slug is
 * unknown: a dashboard published moments ago in the CMS is missing from the
 * cached list, which has a 100s staleTime.
 */
const ensurePublishedDashboardBySlug = async (
  queryClient: QueryClient,
  dashboardSlug?: string,
) => {
  const cached = await queryClient.ensureQueryData(publishedDashboardsQuery());
  const dashboard = getDashboardBySlug(cached, dashboardSlug);

  if (dashboard) return dashboard;

  const dashboards = await queryClient.fetchQuery({
    ...publishedDashboardsQuery(),
    staleTime: 0,
  });

  return getDashboardBySlug(dashboards, dashboardSlug);
};

/**
 * Root Loader
 */
export const rootLoader = () => {
  const isAuthenticated = getIsAuthenticated();

  if (!isAuthenticated) {
    return redirect("/general/information");
  }

  return redirect("/dashboards");
};

/**
 * Loader for al the pages
 */
export const pageLoader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const { dashboard: dashboardSlug, page: pageSlug, subPage: subPageSlug } = params;

    const dashboard = await ensurePublishedDashboardBySlug(queryClient, dashboardSlug);

    assert(dashboard !== undefined);

    // Retries while the publication is not visible on the published endpoints
    // yet, and throws the 404 itself once it is clear the slug does not exist.
    const page = await ensurePublishedNavigationElement(
      queryClient,
      String(dashboard?.id),
      pageSlug,
      subPageSlug,
    );

    if (pageHasChildren(page)) {
      const firstChildSlug = getFirstNavigationElementUrl(
        page?.children as PublishedNavigationElement[],
      );

      return redirect(String(firstChildSlug));
    }

    return null;
  };

export type PageLoaderData = ReturnTypeFromAsync<typeof pageLoader>;

/**
 * Main loader when not opening a page directly, this loader will
 */
export const dashboardsLoader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const dashboards = await queryClient.ensureQueryData(publishedDashboardsQuery());

    const dashboard = getDashboardBySlug(dashboards, params.dashboard);

    assert(dashboard !== undefined);

    const navigationElements = await queryClient.ensureQueryData(
      publishedNavigationElementsQuery(String(dashboard?.id)),
    );

    const url = getFirstNavigationElementUrl(navigationElements);

    assert(url !== undefined);

    return redirect(String(url));
  };
